import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { BellIcon, HeartIcon, ChatBubbleLeftIcon, MagnifyingGlassIcon,
         BookmarkIcon, UserCircleIcon, Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import useAuthStore from '../store/authStore';

const navLinks = [
  { label: 'Search',      href: '/search',       icon: MagnifyingGlassIcon },
  { label: 'Interests',   href: '/interests',    icon: HeartIcon },
  { label: 'Shortlist',   href: '/shortlist',    icon: BookmarkIcon },
  { label: 'Chat',        href: '/chat',         icon: ChatBubbleLeftIcon },
];

export default function Navbar() {
  const { user, logout } = useAuthStore();
  const navigate         = useNavigate();
  const location         = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  const active = (href) => location.pathname.startsWith(href);

  return (
    <nav className="bg-primary shadow-lg sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 flex items-center justify-between h-16">
        {/* Logo */}
        <Link to="/dashboard" className="flex items-center gap-2">
          <img src="/cross-icon.png" alt="Cross" className="w-6 h-6 rounded object-cover" />
          <span className="text-white font-heading font-bold text-xl leading-tight">
            Holy<span className="text-gold-400">weds</span>
          </span>
        </Link>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-1">
          {navLinks.map(({ label, href, icon: Icon }) => (
            <Link key={href} to={href}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                active(href)
                  ? 'bg-white/20 text-white'
                  : 'text-primary-100 hover:text-white hover:bg-white/10'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </Link>
          ))}
        </div>

        {/* Right side */}
        <div className="hidden md:flex items-center gap-2">
          <Link to="/notifications" className="relative p-2 text-primary-100 hover:text-white">
            <BellIcon className="w-5 h-5" />
          </Link>

          <div className="relative group">
            <button className="flex items-center gap-2 text-white hover:bg-white/10 px-3 py-2 rounded-lg transition-colors">
              <UserCircleIcon className="w-5 h-5" />
              <span className="text-sm font-medium">{user?.firstName || 'Me'}</span>
            </button>
            <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-[100]">
              <Link to="/profile/me"   className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50">My Profile</Link>
              <Link to="/profile/setup" className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50">Edit Profile</Link>
              <Link to="/subscription" className="block px-4 py-3 text-sm text-gold font-semibold hover:bg-gray-50">✨ Upgrade Plan</Link>
              <hr className="border-gray-100" />
              <button onClick={logout} className="block w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-red-50">
                Sign Out
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu button */}
        <button className="md:hidden text-white p-2" onClick={() => setMenuOpen(!menuOpen)}>
          {menuOpen ? <XMarkIcon className="w-6 h-6" /> : <Bars3Icon className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden bg-primary-700 border-t border-primary-600 px-4 py-3 space-y-1">
          {navLinks.map(({ label, href, icon: Icon }) => (
            <Link key={href} to={href} onClick={() => setMenuOpen(false)}
              className="flex items-center gap-2 px-3 py-2 text-primary-100 hover:text-white hover:bg-white/10 rounded-lg text-sm">
              <Icon className="w-4 h-4" />
              {label}
            </Link>
          ))}
          <hr className="border-primary-600 my-2" />
          <Link to="/profile/me"   onClick={() => setMenuOpen(false)} className="block px-3 py-2 text-primary-100 text-sm">My Profile</Link>
          <Link to="/subscription" onClick={() => setMenuOpen(false)} className="block px-3 py-2 text-gold-400 text-sm font-semibold">✨ Upgrade Plan</Link>
          <button onClick={logout} className="block w-full text-left px-3 py-2 text-red-400 text-sm">Sign Out</button>
        </div>
      )}
    </nav>
  );
}
